import React, { ChangeEvent, CSSProperties, FC, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { OneInchToken } from '@api';

import { DropdownItem } from '@uikit/components/Dropdown';
import { Search } from '@uikit/icons/Search';

import { TokenOption } from '@magic-swap/components/pages/MagicSwap/ui/TokenOption';

interface TokensSelectProps {
  tokens: OneInchToken[];
  onTokenSelect: (token: OneInchToken) => void;
  fetchTokenBalance: () => Promise<void>;
}

const renderTokenItem = ({
  index,
  style,
  data,
}: {
  index: number;
  style: CSSProperties;
  data: {
    onSelect: (token: OneInchToken) => void;
    tokens: OneInchToken[];
  };
}) => {
  const token = data.tokens[index];
  return (
    <DropdownItem
      key={token.attributes.name}
      style={style}
      onClick={() => data.onSelect(token)}
      label={token.attributes.name}
    >
      <TokenOption token={token} className="text-xs sm:!text-lg" />
    </DropdownItem>
  );
};

const TokensSelect: FC<TokensSelectProps> = ({ tokens, fetchTokenBalance, onTokenSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTokens = useMemo(
    () =>
      tokens.filter(
        (token) =>
          token.attributes.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          token.attributes.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [tokens, searchTerm],
  );

  const favoriteTokens = useMemo(
    () => tokens.filter((token) => token.attributes.favorite),
    [tokens],
  );

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(value);

    fetchTokenBalance();
  };

  return (
    <>
      <div className="flex gap-3 items-center px-4 border border-primary rounded-full h-10">
        <Search className="icon" />

        <input
          className="bg-transparent outline-none w-full"
          value={searchTerm}
          onChange={handleChange}
          placeholder="Search Crypto"
        />
      </div>

      {!!favoriteTokens.length && (
        <div className="flex gap-1 flex-wrap my-4">
          {favoriteTokens.map((token) => (
            <TokenOption
              key={token.id}
              hideName
              selectedView
              token={token}
              className="max-sm:text-xs font-medium px-2 sm:px-3 py-1 sm:py-2 rounded-xl hover:bg-primary-100 border border-neutral-200 cursor-pointer"
              onClick={() => onTokenSelect(token)}
            />
          ))}
        </div>
      )}

      <List
        height={400}
        itemCount={filteredTokens.length}
        itemSize={56}
        width="100%"
        itemData={{ tokens: filteredTokens, onSelect: onTokenSelect }}
      >
        {renderTokenItem}
      </List>
    </>
  );
};

export default TokensSelect;
